import { responsiveFontSizes } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import shadows from './shadows';
import { light, dark } from './palette';

const getTheme = (mode, themeToggler) =>
    responsiveFontSizes(
        createTheme({
            palette: mode === 'dark' ? dark : light,
            shadows: shadows(mode),
            typography: {
                fontFamily: '"Lato", sans-serif',
                button: {
                    textTransform: 'none',
                    fontWeight: 'medium',
                },
            },
            zIndex: {
                appBar: 1200,
                drawer: 1300,
            },
            components: {
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontWeight: 400,
                            borderRadius: 5,
                            paddingTop: 10,
                            paddingBottom: 10,
                        },
                        containedSecondary: mode === 'light' ? { color: 'white' } : {},
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: {
                            borderRadius: 5,
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            borderRadius: 5,
                        },
                        input: {
                            borderRadius: 5,
                        },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            borderRadius: 8,
                        },
                    },
                },
                MuiCssBaseline: {
                    styleOverrides: (theme) => {
                        const tokens = theme.palette.libretto || {};
                        return {
                            ':root': {
                                '--app-bg-canvas': tokens.bgCanvas || '#F5F6F7',
                                '--app-bg-surface': tokens.bgSurface || '#FFFFFF',
                                '--app-bg-subtle': tokens.bgSubtle || '#F3F4F6',
                                '--app-border-subtle': tokens.borderSubtle || '#E5E7EB',
                                '--app-border-muted': tokens.borderMuted || '#D1D5DB',
                                '--app-timeline-clip-border': tokens.timelineClipBorder || tokens.borderMuted || '#94A3B8',
                                '--app-timeline-clip-border-strong': tokens.timelineClipBorderStrong || tokens.textMuted || '#64748B',
                                '--app-timeline-clip-selection': tokens.timelineClipSelection || '#2B6BFD',
                                '--app-timeline-trim-handle-surface': tokens.timelineTrimHandleSurface || '#F8FAFC',
                                '--app-timeline-trim-handle-grip': tokens.timelineTrimHandleGrip || '#2B6BFD',
                                '--app-video-clip-surface': tokens.videoClipSurface || '#BFDDE8',
                                '--app-video-clip-surface-hover': tokens.videoClipSurfaceHover || '#B4D5E2',
                                '--app-video-clip-border': tokens.videoClipBorder || '#5F8292',
                                '--app-video-clip-text': tokens.videoClipText || '#213E4B',
                                '--app-audio-clip-surface': tokens.audioClipSurface || '#D8D1F0',
                                '--app-audio-clip-surface-hover': tokens.audioClipSurfaceHover || '#CDC5EA',
                                '--app-audio-clip-border': tokens.audioClipBorder || '#766EA1',
                                '--app-audio-clip-text': tokens.audioClipText || '#38325F',
                                '--app-image-clip-surface': tokens.imageClipSurface || '#A9DDBF',
                                '--app-image-clip-surface-hover': tokens.imageClipSurfaceHover || '#9ED4B5',
                                '--app-image-clip-border': tokens.imageClipBorder || '#4F8869',
                                '--app-image-clip-text': tokens.imageClipText || '#214C36',
                                '--app-captions-clip-surface': tokens.captionsClipSurface || '#D8DCE3',
                                '--app-captions-clip-surface-hover': tokens.captionsClipSurfaceHover || '#CDD2DA',
                                '--app-captions-clip-border': tokens.captionsClipBorder || '#747E8C',
                                '--app-captions-clip-text': tokens.captionsClipText || '#343B47',
                                '--app-text-clip-surface': tokens.textClipSurface || '#E8D7B7',
                                '--app-text-clip-surface-hover': tokens.textClipSurfaceHover || '#DECAA5',
                                '--app-text-clip-border': tokens.textClipBorder || '#927A54',
                                '--app-text-clip-text': tokens.textClipText || '#4D3A20',
                                '--app-script-clip-surface': tokens.scriptClipSurface || '#C8D5E3',
                                '--app-script-clip-surface-hover': tokens.scriptClipSurfaceHover || '#C2D0DF',
                                '--app-script-clip-border': tokens.scriptClipBorder || '#60748A',
                                '--app-script-clip-text': tokens.scriptClipText || '#293B4F',
                                '--app-script-clip-waveform': tokens.scriptClipWaveform || '#374151',
                                '--app-script-clip-divider': tokens.scriptClipDivider || 'rgba(51, 65, 85, 0.66)',
                                '--app-script-clip-divider-strong': tokens.scriptClipDividerStrong || 'rgba(51, 65, 85, 0.82)',
                                '--app-script-clip-divider-highlight': tokens.scriptClipDividerHighlight || 'rgba(255, 255, 255, 0.20)',
                                '--app-script-clip-cut': tokens.scriptClipCut || 'rgba(51, 65, 85, 0.58)',
								'--app-script-clip-gap-hover': tokens.scriptClipGapHover || 'rgba(51, 65, 85, 0.11)',
                                '--app-script-clip-inset-highlight': tokens.scriptClipInsetHighlight || 'rgba(255, 255, 255, 0.42)',
                                '--app-text-primary': tokens.textPrimary || '#1A1A1A',
                                '--app-text-muted': tokens.textMuted || '#6B7280',
                                '--app-text-subtle': tokens.textSubtle || '#9CA3AF',
                                '--app-icon-primary': tokens.iconPrimary || '#4B5563',
                                '--app-highlight-filler': tokens.fillerHighlight || '#FEF3C7',
                                '--app-highlight-filler-active': tokens.fillerHighlightActive || '#FED7AA',
                            },
                            html: {
                                backgroundColor: 'var(--app-bg-canvas)',
                            },
                            body: {
                                backgroundColor: 'var(--app-bg-canvas)',
                                color: 'var(--app-text-primary)',
                            },
                            '#root': {
                                backgroundColor: 'var(--app-bg-canvas)',
                                color: 'var(--app-text-primary)',
                            },
                        };
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundColor: 'var(--app-bg-surface)',
                            color: 'var(--app-text-primary)',
                        },
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: 'var(--app-bg-surface)',
                            color: 'var(--app-text-primary)',
                            border: '1px solid var(--app-border-subtle)',
                        },
                    },
                },
                MuiMenu: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: 'var(--app-bg-surface)',
                            color: 'var(--app-text-primary)',
                            border: '1px solid var(--app-border-subtle)',
                        },
                    },
                },
                MuiPopover: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: 'var(--app-bg-surface)',
                            color: 'var(--app-text-primary)',
                            border: '1px solid var(--app-border-subtle)',
                        },
                    },
                },
            },
            breakpoints: {
                values: {
                    xs: 0,
                    sm: 600,
                    smd: 1050,
                    md: 1150,
                    lmd: 1280,
                    lg: 1300,
                    xl: 1536,
                },
            },
            themeToggler,
        }),
    );

export default getTheme;
